import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase, Rocket, ArrowRight, Check } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SparkLogo from '../components/SparkLogo';

const ROLE_STORAGE_KEY = 'getspark_pending_role';

const readPendingRole = (search) => {
  // Priority: 1) explicit query param  2) sessionStorage  3) null
  try {
    const params = new URLSearchParams(search || '');
    const q = params.get('role');
    if (q === 'investor' || q === 'entrepreneur') return q;
  } catch (_) {}
  try {
    const saved = sessionStorage.getItem(ROLE_STORAGE_KEY);
    if (saved === 'investor' || saved === 'entrepreneur') return saved;
  } catch (_) {}
  return null;
};

const Onboarding = () => {
  const { profile, updateProfile } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const [role, setRole] = useState(() => {
    // 1. existing profile role  2. query param  3. pending role from sessionStorage  4. null
    if (profile?.role) return profile.role;
    return readPendingRole(routerLocation.search);
  });

  // Sync role once profile hydrates (avoid overwriting user's manual selection)
  useEffect(() => {
    if (!role) {
      const pending = profile?.role || readPendingRole(routerLocation.search);
      if (pending) setRole(pending);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.role]);
  const [step, setStep] = useState(1);
  const [bio, setBio] = useState(profile?.bio || '');
  const [location, setLocation] = useState(profile?.location || '');
  const [loading, setLoading] = useState(false);

  // Once we are mounted with a pre-selected role, clear the storage so it doesn't
  // override future flows (e.g. user toggling role)
  useEffect(() => {
    try { sessionStorage.removeItem(ROLE_STORAGE_KEY); } catch (_) {}
  }, []);

  const handleFinish = async () => {
    if (!role) return;
    setLoading(true);
    try {
      await updateProfile({ role, bio, location, onboarded: true });
      navigate(role === 'entrepreneur' ? '/app/business/new' : '/app/feed', { replace: true });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050816] text-white flex items-center justify-center px-5 py-12 relative">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(79,172,254,0.15), transparent 70%)' }} />
        <div className="absolute bottom-0 right-1/3 w-[500px] h-[500px] rounded-full" style={{ background: 'radial-gradient(circle, rgba(255,167,38,0.15), transparent 70%)' }} />
      </div>

      <motion.div initial={{opacity:0, y:20}} animate={{opacity:1, y:0}} transition={{duration:0.6}} className="relative w-full max-w-2xl">
        <div className="flex justify-center mb-6"><SparkLogo height={64} /></div>

        <div className="glass-card rounded-3xl p-7 sm:p-10">
          {step === 1 ? (
            <>
              <span className="eyebrow eyebrow-gold">Passo 1 de 2</span>
              <h1 className="heading-display text-3xl sm:text-4xl mt-4">Como você quer usar o <span className="gradient-text">GetSpark</span>?</h1>
              <p className="mt-2 text-sm text-slate-400">Escolha seu perfil — você pode mudar depois.</p>

              <div className="grid sm:grid-cols-2 gap-4 mt-7">
                {[
                  { id: 'entrepreneur', icon: Rocket, title: 'Tenho uma ideia', desc: 'Quero apresentar meu negócio e encontrar investidores.', accent: 'gold' },
                  { id: 'investor', icon: Briefcase, title: 'Quero investir', desc: 'Quero descobrir negócios e apoiar pequenos empreendedores.', accent: 'blue' },
                ].map((opt) => (
                  <button key={opt.id} onClick={() => setRole(opt.id)}
                    data-testid={`onboarding-role-${opt.id}`}
                    className={`relative spotlight-card rounded-2xl p-5 text-left transition-all ${
                      role === opt.id
                        ? opt.accent === 'gold' ? 'glass-card-gold ring-2 ring-[#ffa726]/60' : 'glass-card-blue ring-2 ring-[#4facfe]/60'
                        : 'glass-card hover:bg-white/[0.06]'
                    }`}>
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${
                      opt.accent === 'gold' ? 'bg-[#ffa726]/20 text-[#ffd54f]' : 'bg-[#4facfe]/20 text-[#4facfe]'
                    }`}><opt.icon size={20} /></div>
                    <div className="font-bold text-white">{opt.title}</div>
                    <p className="mt-1 text-xs text-slate-400 leading-relaxed">{opt.desc}</p>
                    {role === opt.id && (
                      <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-gradient-to-br from-[#4facfe] to-[#ffa726] flex items-center justify-center"><Check size={13} /></div>
                    )}
                  </button>
                ))}
              </div>

              <button disabled={!role} onClick={() => setStep(2)} data-testid="onboarding-step1-continue" className="mt-7 w-full btn-primary rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                Continuar <ArrowRight size={14} />
              </button>
            </>
          ) : (
            <>
              <span className="eyebrow">Passo 2 de 2</span>
              <h1 className="heading-display text-3xl sm:text-4xl mt-4">Conte um pouco sobre você</h1>
              <p className="mt-2 text-sm text-slate-400">Isso ajuda outras pessoas a entender quem você é.</p>

              <div className="mt-6 space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400 mb-1.5 block">Bio</label>
                  <textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={280} rows={3}
                    placeholder={role === 'entrepreneur' ? 'Ex: Dona da Cafeteria Beatriz. Quero expandir para mais 2 unidades.' : 'Ex: Investidor apaixonado por pequenos negócios e ideias inovadoras.'}
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50 resize-none" />
                  <div className="text-[10px] text-slate-500 mt-1 text-right">{bio.length}/280</div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-slate-400 mb-1.5 block">Cidade</label>
                  <input type="text" value={location} onChange={(e) => setLocation(e.target.value)}
                    placeholder="Ex: São Paulo, SP"
                    className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#4facfe]/50" />
                </div>
              </div>

              <div className="mt-7 flex gap-3">
                <button onClick={() => setStep(1)} className="flex-1 btn-secondary rounded-xl py-3 text-sm font-semibold">Voltar</button>
                <button disabled={loading} onClick={handleFinish} className="flex-1 btn-gold rounded-xl py-3 text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-50">
                  {loading ? 'Salvando...' : <>Acender minha Spark <ArrowRight size={14} /></>}
                </button>
              </div>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default Onboarding;
